import { useEffect, useRef, useState } from 'react';
import ChartistGraph from 'react-chartist';

function formatYAxisValue(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '';
  }
  const absValue = Math.abs(value);
  if (absValue >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  }
  if (absValue >= 1_000) {
    return `$${(value / 1_000).toFixed(0)}K`;
  }
  return `$${Math.round(value)}`;
}

function formatDayChange(dayChange, formatter) {
  const { dollarChange, percentChange } = dayChange;
  if (!Number.isFinite(dollarChange) || !Number.isFinite(percentChange)) {
    return null;
  }
  const dollarPrefix = dollarChange < 0 ? '-' : '';
  const percentPrefix = percentChange >= 0 ? '+' : '';
  const dollarText = `${dollarPrefix}$${formatter.format(Math.abs(dollarChange))}`;
  const percentText = `${percentPrefix}${percentChange.toFixed(2)}%`;
  return `Today: ${dollarText} (${percentText})`;
}

function PortfolioValueCard(props) {
  const chartSurfaceRef = useRef(null);
  const [chartHeight, setChartHeight] = useState(200);
  const formatter = Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2
  });
  const labelCount = props.chartData?.labels?.length || 0;
  const labelStep = Math.max(1, Math.ceil(labelCount / 6));

  useEffect(() => {
    const element = chartSurfaceRef.current;
    if (!element) {
      return undefined;
    }

    const updateHeight = () => {
      const height = element.clientHeight;
      if (height > 0) {
        setChartHeight(height);
      }
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const options = {
      height: `${chartHeight}px`,
      fullWidth: true,
      showArea: true,
      showLine: false,
      showPoint: false,
      chartPadding: {
        left: 5,
        right: 25
      },
      axisX: {
        showLabel: true,
        showGrid: false,
        labelInterpolationFnc: (value, index) => {
          if (index % labelStep !== 0 && index !== labelCount - 1) {
            return null;
          }
          return value;
        }
      },
      axisY: {
        scaleMinSpace: 35,
        labelInterpolationFnc: formatYAxisValue
      },
      lineSmooth: false
  };

  return (
    <div className="col-12 col-xl-6">
      <div className="card shadow-sm portfolio-value-card">
        <div className="card-header d-flex flex-row align-items-start flex-0 portfolio-value-header">
          <div className="h4 col text-left mb-0">Portfolio Value</div>
          <div className="col text-right">
            <div className="h2 mb-0">${formatter.format(Number.isFinite(props.portfolioValue) ? props.portfolioValue : 0)}</div>
            {props.dayChange && (
              <div className={`portfolio-value-day-change ${props.dayChange.dollarChange >= 0 ? 'text-success' : 'text-danger'}`}>
                {formatDayChange(props.dayChange, formatter)}
              </div>
            )}
          </div>
        </div>
        <div className="card-body portfolio-value-chart">
          <div className="portfolio-value-chart-surface" ref={chartSurfaceRef}>
            <ChartistGraph data={props.chartData} type="Line" options={options} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default PortfolioValueCard;
